import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import PageMeta from "@/components/feature/PageMeta";
import TopicCard from "@/components/base/TopicCard";
import ArticleAside from "./components/ArticleAside";
import DialogueThread from "./components/DialogueThread";
import { formatDateC, getCategoryName, getRelatedTopics, getTopicBySlug } from "@/lib/content";

export default function TopicDetail() {
  const { slug } = useParams<{ slug: string }>();
  const topic = slug ? getTopicBySlug(slug) : undefined;

  if (!topic) {
    return (
      <SiteLayout>
        <PageMeta
          title="档案不存在 · 黑料吃话"
          description="你访问的话题档案可能已被移动或尚未整理完成，可以返回归档库继续检索。"
          path="/topics"
        />
        <section className="mt-[68px] flex flex-col items-center justify-center bg-background-50 px-4 py-24 text-center md:py-32">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-3xl text-primary-500">
            <i className="ri-file-unknow-line" />
          </span>
          <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950">
            这份档案暂时找不到
          </h1>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-foreground-600">
            它可能已被移动，或尚未完成整理。你可以返回归档库，用关键词重新检索。
          </p>
          <Link
            to="/topics"
            className="mt-6 inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
          >
            返回归档库
            <i className="ri-arrow-right-line" />
          </Link>
        </section>
      </SiteLayout>
    );
  }

  const categoryName = getCategoryName(topic.category);
  const related = getRelatedTopics(topic.slug, 3);

  return (
    <SiteLayout>
      <PageMeta
        title={`${topic.title} · 黑料吃话文字版`}
        description={`${topic.summary.slice(0, 90)}… 黑料吃话整理的事件时间线、对话实录、各方回应与观察笔记。`}
        keywords={`${topic.title},黑料吃话文字版,${topic.tags.join(",")},${categoryName}`}
        path={`/topics/${topic.slug}`}
        type="article"
        image={topic.cover}
        breadcrumbs={[
          { name: "首页", path: "/" },
          { name: "话题归档", path: "/topics" },
          { name: topic.title, path: `/topics/${topic.slug}` },
        ]}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: topic.title,
          description: topic.summary,
          datePublished: topic.date,
          dateModified: topic.date,
          articleSection: categoryName,
          keywords: topic.tags.join(","),
          image: topic.cover,
          author: { "@type": "Organization", name: "黑料吃话" },
          publisher: { "@type": "Organization", name: "黑料吃话" },
        }}
      />

      <header className="relative mt-[68px] border-b border-background-200 bg-background-100 px-4 pb-10 pt-12 md:px-8 md:pb-14 md:pt-16">
        <div className="paper-grid absolute inset-0 opacity-40" />
        <div className="relative mx-auto w-full max-w-8xl">
          <nav
            aria-label="面包屑导航"
            className="flex flex-wrap items-center gap-2 text-xs text-foreground-500"
          >
            <Link to="/" className="transition-colors hover:text-primary-600">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-300" />
            <Link to="/topics" className="transition-colors hover:text-primary-600">
              话题归档
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-300" />
            <span className="line-clamp-1 text-foreground-800">{topic.title}</span>
          </nav>

          <div className="mt-6 flex flex-wrap items-center gap-3 text-xs">
            <span className="rounded-full bg-secondary-100 px-3 py-1 font-medium text-secondary-900">
              {categoryName}
            </span>
            {topic.hot ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-3 py-1 font-medium text-primary-700">
                <i className="ri-fire-line" />
                热议档案
              </span>
            ) : null}
          </div>

          <h1 className="mt-4 max-w-4xl font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
            {topic.title}
          </h1>

          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-500">
            <span className="flex items-center gap-1 whitespace-nowrap">
              <i className="ri-calendar-line" />
              {formatDateC(topic.date)}
            </span>
            <span className="flex items-center gap-1 whitespace-nowrap">
              <i className="ri-time-line" />
              {topic.readTime}
            </span>
            <span className="flex items-center gap-1 whitespace-nowrap">
              <i className="ri-eye-line" />
              {topic.views} 阅读
            </span>
          </div>
        </div>
      </header>

      <div className="bg-background-50 px-4 py-10 md:px-8 md:py-14">
        <div className="mx-auto w-full max-w-8xl">
          <div className="overflow-hidden rounded-lg border border-background-200 bg-background-200">
            <img
              src={topic.cover}
              alt={`${topic.title} 档案封面`}
              title={`${topic.title} - 黑料吃话文字整理`}
              className="h-56 w-full object-cover object-top md:h-96"
            />
          </div>

          <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-12">
            <article className="lg:col-span-8">
              <section id="overview" className="scroll-mt-28">
                <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                  <span className="h-5 w-1 rounded-full bg-primary-500" />
                  事件概述
                </h2>
                <p className="mt-4 border-l-2 border-accent-400 bg-background-100 px-5 py-4 text-sm leading-relaxed text-foreground-700 md:text-base">
                  {topic.summary}
                </p>
              </section>

              <section id="timeline" className="mt-12 scroll-mt-28">
                <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                  <span className="h-5 w-1 rounded-full bg-primary-500" />
                  时间线还原
                </h2>
                <ol className="mt-6 border-l border-background-300 pl-6">
                  {topic.timeline.map((item, index) => (
                    <li key={`${item.time}-${index}`} className="relative pb-6 last:pb-0">
                      <span className="absolute -left-[31px] top-1 flex h-3 w-3 items-center justify-center rounded-full border-2 border-primary-500 bg-background-50" />
                      <span className="inline-flex rounded-full bg-secondary-100 px-2.5 py-0.5 font-heading text-xs font-medium text-secondary-900">
                        {item.time}
                      </span>
                      <p className="mt-2 text-sm leading-relaxed text-foreground-700">{item.text}</p>
                    </li>
                  ))}
                </ol>
              </section>

              <section id="dialogue" className="mt-12 scroll-mt-28">
                <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                  <span className="h-5 w-1 rounded-full bg-primary-500" />
                  对话实录
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                  以下内容按原始语境整理，保留表达原有的语气与顺序，未做改写。
                </p>
                <div className="mt-2 rounded-lg border border-background-200 bg-background-50 p-4 md:p-6">
                  <DialogueThread dialogues={topic.dialogues} />
                </div>
              </section>

              <section id="responses" className="mt-12 scroll-mt-28">
                <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                  <span className="h-5 w-1 rounded-full bg-primary-500" />
                  各方回应
                </h2>
                <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {topic.responses.map((item) => (
                    <div
                      key={item.party}
                      className="rounded-lg border border-background-200 bg-background-100 p-5"
                    >
                      <h3 className="flex items-center gap-2 font-heading text-sm font-semibold text-foreground-950">
                        <i className="ri-account-circle-line text-base text-secondary-500" />
                        {item.party}
                      </h3>
                      <p className="mt-2.5 text-sm leading-relaxed text-foreground-600">
                        {item.content}
                      </p>
                    </div>
                  ))}
                </div>
              </section>

              <section id="notes" className="mt-12 scroll-mt-28">
                <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                  <span className="h-5 w-1 rounded-full bg-primary-500" />
                  观察笔记
                </h2>
                <ul className="mt-6 space-y-3">
                  {topic.notes.map((note, index) => (
                    <li
                      key={note}
                      className="flex gap-3 rounded-lg border border-background-200 bg-background-50 p-4"
                    >
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-accent-100 font-heading text-xs font-bold text-accent-800">
                        {index + 1}
                      </span>
                      <p className="text-sm leading-relaxed text-foreground-700">{note}</p>
                    </li>
                  ))}
                </ul>
              </section>

              <div className="mt-10 flex flex-wrap items-center gap-2 border-t border-background-200 pt-6">
                <span className="text-xs text-foreground-500">相关标签：</span>
                {topic.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/topics?q=${encodeURIComponent(tag)}`}
                    className="whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900 transition-colors hover:bg-secondary-200"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            </article>

            <aside className="lg:col-span-4">
              <ArticleAside topic={topic} />
            </aside>
          </div>

          <section className="mt-16 border-t border-background-200 pt-12">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                相关档案推荐
              </h2>
              <Link
                to="/topics"
                className="inline-flex items-center gap-1 whitespace-nowrap text-sm text-primary-600 transition-colors hover:text-primary-700"
              >
                查看全部归档
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((item) => (
                <TopicCard key={item.id} topic={item} />
              ))}
            </div>
          </section>
        </div>
      </div>
    </SiteLayout>
  );
}